import {
  ColumnTypeMap,
  ColumnValueType,
  isBlank,
  parseDateCell,
  parseNumericCell,
} from "./cellValue";
import { SheetRow, SheetSource } from "./sheetTypes";
import { SortMode, SortRule } from "./sheetViewState";

/**
 * Ordering rules, matching what a person expects from Excel:
 *
 * - blanks sink to the bottom in BOTH directions (they are absence, not a value);
 * - numbers and dates order by value, never lexically ("980" before "1,234.56");
 * - text orders naturally, so "item2" comes before "item10" and "007" before "8";
 * - ties keep the file's own order, so a sort is stable and repeatable.
 */

let sharedCollator: Intl.Collator | null = null;

/** Collators are expensive to build and stateless once built - make one. */
export function sharedTextCollator(): Intl.Collator {
  if (!sharedCollator) {
    sharedCollator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }
  return sharedCollator;
}

type SortKind = "number" | "date" | "text";

export function resolveSortKind(
  mode: SortMode,
  type: ColumnValueType | undefined,
): SortKind {
  if (mode === "number") return "number";
  if (mode === "date") return "date";
  if (mode === "text") return "text";
  if (type === "number" || type === "mixed") return "number";
  if (type === "date") return "date";
  return "text";
}

interface PreparedRule {
  kind: SortKind;
  direction: number;
  /** NaN marks a non-blank cell that did not parse - it sorts after real values. */
  numeric: Float64Array | null;
  blank: Uint8Array;
  text: string[];
}

/**
 * Keys are computed once per row (a Schwartzian transform) rather than inside
 * the comparator: sorting 50k rows calls the comparator around 800k times, and
 * parsing "$1,234.56" that often is what turns a 50 ms sort into a 2 s freeze.
 */
function prepareRule(
  rows: SheetRow[],
  source: SheetSource,
  rule: SortRule,
  types: ColumnTypeMap,
): PreparedRule | null {
  const column = source.columns.find((c) => c.id === rule.columnId);
  if (!column) return null;

  const kind = resolveSortKind(rule.mode, types[rule.columnId]);
  const count = rows.length;
  const blank = new Uint8Array(count);
  const text: string[] = new Array(count);
  const numeric = kind === "text" ? null : new Float64Array(count);

  for (let i = 0; i < count; i++) {
    const raw = rows[i].cells[column.sourceIndex];
    const value = raw == null ? "" : raw;
    text[i] = value;
    if (isBlank(value)) {
      blank[i] = 1;
      if (numeric) numeric[i] = NaN;
      continue;
    }
    if (numeric) {
      const parsed =
        kind === "number" ? parseNumericCell(value) : parseDateCell(value);
      numeric[i] = parsed === null ? NaN : parsed;
    }
  }

  return {
    kind,
    direction: rule.direction === "desc" ? -1 : 1,
    numeric,
    blank,
    text,
  };
}

function compareAt(
  rule: PreparedRule,
  a: number,
  b: number,
  collator: Intl.Collator,
): number {
  const aBlank = rule.blank[a] === 1;
  const bBlank = rule.blank[b] === 1;
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;

  if (rule.numeric) {
    const av = rule.numeric[a];
    const bv = rule.numeric[b];
    const aNum = !isNaN(av);
    const bNum = !isNaN(bv);
    if (aNum && bNum) {
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * rule.direction;
    }
    // Excel puts text after numbers ascending, and before them descending.
    if (aNum !== bNum) return (aNum ? -1 : 1) * rule.direction;
  }

  return collator.compare(rule.text[a], rule.text[b]) * rule.direction;
}

/**
 * Compares two raw cell values directly. Used by the condition filters and by
 * tests; the grid sorts through sortRows, which precomputes its keys.
 */
export function compareCells(
  a: string,
  b: string,
  kind: SortKind,
  direction: "asc" | "desc" = "asc",
): number {
  const sign = direction === "desc" ? -1 : 1;
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;

  if (kind !== "text") {
    const parse = kind === "number" ? parseNumericCell : parseDateCell;
    const av = parse(a);
    const bv = parse(b);
    if (av !== null && bv !== null) {
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * sign;
    }
    if (av !== null || bv !== null) return (av !== null ? -1 : 1) * sign;
  }
  return sharedTextCollator().compare(a, b) * sign;
}

/**
 * Returns a new array; the input order is the tie-break, and since rows always
 * arrive in file order that means equal values keep their original positions.
 */
export function sortRows(
  rows: SheetRow[],
  rules: SortRule[],
  source: SheetSource,
  types: ColumnTypeMap,
): SheetRow[] {
  if (rules.length === 0 || rows.length < 2) return rows;

  const prepared: PreparedRule[] = [];
  for (let i = 0; i < rules.length; i++) {
    const rule = prepareRule(rows, source, rules[i], types);
    if (rule) prepared.push(rule);
  }
  if (prepared.length === 0) return rows;

  const collator = sharedTextCollator();
  const order: number[] = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) order[i] = i;

  order.sort((a, b) => {
    for (let i = 0; i < prepared.length; i++) {
      const cmp = compareAt(prepared[i], a, b, collator);
      if (cmp !== 0) return cmp;
    }
    return a - b;
  });

  const out: SheetRow[] = new Array(rows.length);
  for (let i = 0; i < order.length; i++) out[i] = rows[order[i]];
  return out;
}
